import { AzureKeyCredential } from '@azure/core-auth';
import type { MapsRouteClient } from '@azure-rest/maps-route';
import MapsRouteV1, {
  toColonDelimitedLatLonString,
} from '@azure-rest/maps-route';
import type { MapsSearchClient as MapsSearchClientV1 } from '@azure-rest/maps-search-v1';
import MapsSearchV1 from '@azure-rest/maps-search-v1';
import type { MapsSearchClient as MapsSearchClientV2 } from '@azure-rest/maps-search-v2';
import MapsSearchV2 from '@azure-rest/maps-search-v2';
import type GeoJSON from 'geojson';
import cacheAsyncFunction from 'utils/cacheAsyncFunction';
import getFeatures from 'utils/getFeatures';
import { getTimeDataFromSeconds } from 'utils/getTimeDataFromSeconds';

import type { DiscoveryService } from './discoveryService';
import { getDiscoveryService } from './discoveryService';
import type {
  GetRouteInstructionsResult,
  LatLonPosition,
  SearchAddressResult,
} from './types/geo';

/**
 * GeoService class provides methods to interact with geolocation and mapping services.
 * It includes functionalities for searching addresses, geocoding, reverse geocoding,
 * and getting route instructions.
 *
 * @class GeoService
 *
 * @property {DiscoveryService} _discoveryClient - Client for Gozio discovery service.
 * @property {Object} _routeClient - Client for route services.
 * @property {Object} _geocodeClient - Client for geocoding services.
 * @property {AzureKeyCredential} _credential - Credential for Azure services.
 * @property {GetRouteInstructionsResult | undefined} _lastRouteInstructionsResult - Last route instructions result.
 * @property {SearchAddressResult[] | undefined} _lastSearchAddressResult - Last search address result.
 * @property {string | undefined} _lastSearchAddressQuery - Last search address query.
 * @property {SearchAddressResult[] | undefined} _lastReverseSearchAddressResult - Last reverse search address result.
 * @property {GeoJSON.Position | undefined} _lastReverseSearchAddressQuery - Last reverse search address query.
 * @property {SearchAddressResult | undefined} _lastGeocodeResult - Last geocode result.
 * @property {string | undefined} _lastGeocodeQuery - Last geocode query.
 * @property {SearchAddressResult[] | undefined} _lastReverseGeocodeResult - Last reverse geocode result.
 * @property {GeoJSON.Position | undefined} _lastReverseGeocodeQuery - Last reverse geocode query.
 * @property {LatLonPosition | undefined} _navigatorWatchPosition - Navigator watch position.
 * @property {string | undefined} _navigatorPermissions - Navigator permissions.
 *
 * @constructor
 * @param {string} _apiKey - API key for authentication with Azure.
 *
 * @method _geolocationWatchPosition - Watches the geolocation position using the browser's navigation API.
 * @method _searchAddress - Searches for an address based on a query.
 * @method _geocode - Geocodes an address query to get address details.
 * @method _reverseGeocode - Reverse geocodes a position to get address details.
 * @method _getRouteInstructions - Gets route instructions between two positions.
 *
 * @method searchAddress - Cached version of _searchAddress.
 * @method geocode - Cached version of _geocode.
 * @method reverseGeocode - Cached version of _reverseGeocode.
 * @method getRouteInstructions - Cached version of _getRouteInstructions.
 *
 * @getter lastSearchAddressQuery - Gets the last search address query.
 * @getter lastRouteInstructionsResult - Gets the last route instructions result.
 * @getter lastGeocodeQuery - Gets the last geocode query.
 * @getter lastGeocodeResult - Gets the last geocode result.
 * @getter lastReverseGeocodeQuery - Gets the last reverse geocode query.
 * @getter lastReverseGeocodeResult - Gets the last reverse geocode result.
 * @getter navigatorWatchPosition - Gets the navigator watch position.
 * @getter geobiasPosition - Gets the geobias position.
 */
export class GeoService {
  private readonly _discoveryClient: DiscoveryService;
  private readonly _routeClient: { [key: string]: MapsRouteClient } = {};
  private readonly _geocodeClient: {
    [key: string]: MapsSearchClientV1 | MapsSearchClientV2;
  } = {};
  private readonly _credential: AzureKeyCredential;
  private _lastRouteInstructionsResult: GetRouteInstructionsResult | undefined;
  private _lastSearchAddressResult: SearchAddressResult[] | undefined;
  private _lastSearchAddressQuery: string | undefined;
  private _lastReverseSearchAddressResult: SearchAddressResult[] | undefined;
  private _lastReverseSearchAddressQuery: GeoJSON.Position | undefined;
  private _lastGeocodeResult: SearchAddressResult | undefined;
  private _lastGeocodeQuery: string | undefined;
  private _lastReverseGeocodeResult: SearchAddressResult[] | undefined;
  private _lastReverseGeocodeQuery: GeoJSON.Position | undefined;
  private _navigatorWatchPosition: LatLonPosition | undefined;
  private _navigatorPermissions: string | undefined;

  /**
   * Constructs an instance of the GeoService class.
   *
   * @param _apiKey - The API key used for authentication with Azure services.
   *
   * @throws {Error} If the Discovery Service is not found.
   *
   * Initializes the following clients:
   * - MapsRouteV1 client.
   * - MapsSearchV1 client.
   * - MapsSearchV2 client.
   *
   * Also starts watching geolocation position and sets up the discovery client if available.
   */
  public constructor(private readonly _apiKey: string) {
    this._credential = new AzureKeyCredential(this._apiKey);
    this._routeClient.v1 = MapsRouteV1(this._credential);
    this._geocodeClient.v1 = MapsSearchV1(this._credential);
    this._geocodeClient.v2 = MapsSearchV2(this._credential);
    this._geolocationWatchPosition();
    const discoveryService = getDiscoveryService();
    if (discoveryService) {
      this._discoveryClient = discoveryService;
    } else {
      throw new Error(`GeoService#constructor: Discovery Service not found`);
    }
  }

  private _geolocationWatchPosition(): void {
    navigator.permissions.query({ name: 'geolocation' }).then((data) => {
      const onSuccess = (position: any) => {
        this._navigatorWatchPosition = {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        };
      };

      this._navigatorPermissions = data.state;

      if (data.state === 'granted') {
        navigator.geolocation.watchPosition(
          onSuccess,
          (error) => {
            console.error('Error getting location:', error);
          },
          { timeout: 5000 },
        );
      } else if (data.state === 'prompt') {
        navigator.geolocation.watchPosition(
          onSuccess,
          (error) => {
            console.error('Error getting location:', error);
          },
          { timeout: 5000 },
        );
      } else if (data.state === 'denied') {
        console.error('Geolocation permission denied');
      }
    });
  }

  /**
   * Searches for an address based on the provided query string.
   *
   * This method performs a geocoding search using the provided query string.
   * It returns an array of `SearchAddressResult` objects containing the search results.
   *
   * @param query - The address query string to search for. Must be at least 2 characters long.
   * @returns A promise that resolves to an array of `SearchAddressResult` objects.
   *
   * @throws Will throw an error if there is an issue fetching the address.
   *
   * @remarks
   * - If the query string is less than 2 characters, an empty array is returned.
   * - If the query string ends with a space or is the same as the last search query, the last search results are returned.
   * - The method uses the `geobiasPosition` to bias the search results based on the user's location.
   * - The search is performed using the `MapsSearchClientV1` client.
   *
   * @example
   * ```typescript
   * const results = await geoService._searchAddress('1600 Amphitheatre Parkway');
   * console.log(results);
   * ```
   */
  public async _searchAddress(query: string): Promise<SearchAddressResult[]> {
    if (query.length < 2) return [];
    if (query[query.length - 1] === ' ') {
      return this._lastSearchAddressResult || [];
    }
    if (query === this._lastSearchAddressQuery) {
      return this._lastSearchAddressResult || [];
    }
    try {
      const position = await this.geobiasPosition;
      const response = await (this._geocodeClient.v1 as MapsSearchClientV1)
        .path('/search/address/{format}', 'json')
        .get({
          queryParameters: {
            query,
            typeahead: true,
            countrySet: ['US'],
            language: 'en-US',
            lat: position?.lat,
            lon: position?.lon,
            'api-version': '1.0',
          },
        });
      if ('results' in response.body) {
        const results: SearchAddressResult[] = response.body.results.map(
          (result) => ({
            id: result.id,
            address: result.address.freeformAddress || '',
            position: [result.position.lat, result.position.lon],
            geolocation: {
              lat: result.position.lat,
              lon: result.position.lon,
            },
          }),
        );
        this._lastSearchAddressResult = results;
        this._lastSearchAddressQuery = query;
        return results;
      }
      throw new Error(`GeoService#searchAddress: No results found`);
    } catch (e) {
      console.error(`GeoService#searchAddress: Error fetching address: ${e}`);
    }
    throw new Error(`GeoService#searchAddress: Error fetching address`);
  }

  /**
   * Geocodes a given query string to retrieve address and position information.
   *
   * @param query - The query string to geocode.
   * @returns A promise that resolves to a `SearchAddressResult` containing the address and position.
   * @throws Will throw an error if no address or position is found, or if there is an error fetching the address.
   *
   * @remarks
   * If the query matches the last geocode query, the cached result is returned.
   * Otherwise, it sends a request to the geocode client to fetch the address and position.
   *
   * @example
   * ```typescript
   * const result = await geoService._geocode('1600 Amphitheatre Parkway, Mountain View, CA');
   * console.log(result.address); // Formatted address
   * console.log(result.position); // [latitude, longitude]
   * ```
   */
  private async _geocode(query: string): Promise<SearchAddressResult> {
    try {
      if (
        this._lastGeocodeQuery &&
        this._lastGeocodeResult &&
        query === this._lastGeocodeQuery
      ) {
        return Promise.resolve(this._lastGeocodeResult);
      }

      this._lastGeocodeQuery = query;

      const response = await (this._geocodeClient.v2 as MapsSearchClientV2)
        .path('/geocode')
        .get({
          queryParameters: { query },
        });
      if (response?.body && 'features' in response.body) {
        const addressResult =
          response.body.features?.[0].properties?.address?.formattedAddress;
        const positionResult =
          response.body.features?.[0].properties?.geocodePoints?.[0]?.geometry
            ?.coordinates;

        if (!addressResult || !positionResult) {
          throw new Error(`GeoService#geocode: No address or position found`);
        }

        const result = {
          address: addressResult,
          position: [positionResult[1], positionResult[0]] as [number, number],
          geolocation: {
            lat: positionResult[1],
            lon: positionResult[0],
          },
        };
        this._lastGeocodeResult = result;
        return result;
      }
    } catch (e) {
      throw new Error(`GeoService#geocode: Error fetching address: ${e}`);
    }
    throw new Error(`GeoService#geocode: Error fetching address`);
  }

  /**
   * Performs a reverse geocoding operation to convert geographic coordinates into a human-readable address.
   *
   * @param query - A GeoJSON.Position array containing the latitude and longitude to be reverse geocoded.
   * @returns A promise that resolves to an array of SearchAddressResult objects containing the address and position details.
   * @throws An error if the reverse geocoding operation fails.
   *
   * @example
   * ```typescript
   * const position: GeoJSON.Position = [longitude, latitude];
   * const results = await geoService._reverseGeocode(position);
   * console.log(results);
   * ```
   */
  private async _reverseGeocode(
    query: GeoJSON.Position,
  ): Promise<SearchAddressResult[]> {
    try {
      const response = await (this._geocodeClient.v1 as MapsSearchClientV1)
        .path('/search/address/reverse/{format}', 'json')
        .get({
          queryParameters: {
            query,
          },
        });

      if ('addresses' in response.body) {
        const results: SearchAddressResult[] = response.body.addresses.map(
          (result) => ({
            address: result.address.freeformAddress || '',
            position: [
              parseFloat(result.position.split(',')[0]),
              parseFloat(result.position.split(',')[1]),
            ],
            geolocation: {
              lat: parseFloat(result.position.split(',')[0]),
              lon: parseFloat(result.position.split(',')[1]),
            },
          }),
        );
        this._lastReverseSearchAddressResult = results;
        this._lastReverseSearchAddressQuery = query;
        return results;
      }
    } catch (e) {
      throw new Error(
        `GeoService#reverseSearchAddress: Error fetching address: ${e}`,
      );
    }
    throw new Error(`GeoService#reverseSearchAddress: Error fetching address`);
  }

  /**
   * Fetches route instructions between a start and end position.
   *
   * @param start - The starting position as a GeoJSON.Position array [longitude, latitude].
   * @param end - The ending position as a GeoJSON.Position array [longitude, latitude].
   * @returns A promise that resolves to a GetRouteInstructionsResult object containing route details.
   * @throws Will throw an error if the start or end positions are invalid or if no routes are found.
   *
   * @remarks
   * This method uses the _routeClient to fetch route directions in JSON format. It processes the response
   * to extract route features, travel time, and other relevant details. If the route is successfully fetched,
   * the result is stored in _lastRouteInstructionsResult.
   *
   * @example
   * ```typescript
   * const start: GeoJSON.Position = [-122.4194, 37.7749]; // San Francisco, CA
   * const end: GeoJSON.Position = [-118.2437, 34.0522]; // Los Angeles, CA
   *
   * try {
   *   const result = await geoService._getRouteInstructions(start, end);
   *   console.log(result);
   * } catch (error) {
   *   console.error(error);
   * }
   * ```
   */
  private async _getRouteInstructions(
    start: GeoJSON.Position,
    end: GeoJSON.Position,
    destinationId: number | undefined,
  ): Promise<GetRouteInstructionsResult> {
    if (!start || !end) {
      throw new Error(
        `GeoService#getRouteInstructions: Invalid start or end position`,
      );
    }

    const query = toColonDelimitedLatLonString([
      [start[0], start[1]],
      [end[0], end[1]],
    ]);

    try {
      const response = await this._routeClient.v1
        .path('/route/directions/{format}', 'json')
        .get({
          queryParameters: { query, instructionsType: 'text' },
        });
      if ('routes' in response.body) {
        const route = getFeatures(response.body.routes);
        const feature = route.features[0];
        const { data, description } = getTimeDataFromSeconds(
          feature.properties?.summary.travelTimeInSeconds,
        );

        const routeStart = route.guidance.instructions[0].point;
        const routeEnd = route.guidance.instructions.slice(-1)[0].point;

        const result = {
          startPosition: {
            lat: start[0],
            lon: start[1],
          },
          destinationPosition: {
            lat: end[0],
            lon: end[1],
          },
          routeStart: {
            lat: routeStart?.latitude,
            lon: routeStart?.longitude,
          },
          routeEnd: {
            lat: routeEnd?.latitude,
            lon: routeEnd?.longitude,
          },
          travelTime: {
            data,
            description,
            inSeconds: feature.properties?.summary.travelTimeInSeconds,
          },
          destinationId,
          ...route,
        };
        this._lastRouteInstructionsResult = result;
        return result;
      }

      throw new Error(`GeoService#getRouteInstructions: No routes found`);
    } catch (e) {
      console.error(
        `GeoService#getRouteInstructions: Error fetching route instructions: ${e}`,
      );
    }
    throw new Error(`GeoService#getRouteInstructions: Error fetching route`);
  }

  public searchAddress: (query: string) => Promise<SearchAddressResult[]> =
    cacheAsyncFunction(this._searchAddress.bind(this), 'searchAddress');

  public geocode: (query: string) => Promise<SearchAddressResult> =
    cacheAsyncFunction(this._geocode.bind(this), 'geocode');

  public reverseGeocode: (
    query: GeoJSON.Position,
  ) => Promise<SearchAddressResult[]> = cacheAsyncFunction(
    this._reverseGeocode.bind(this),
    'reverseGeocode',
  );

  public getRouteInstructions: (
    start: GeoJSON.Position,
    end: GeoJSON.Position,
    destinationId: number | undefined,
  ) => Promise<GetRouteInstructionsResult> = cacheAsyncFunction(
    this._getRouteInstructions.bind(this),
    'getRouteInstructions',
  );

  get lastSearchAddressQuery(): string | undefined {
    return this._lastSearchAddressQuery;
  }
  get lastSearchAddressResult(): SearchAddressResult[] | undefined {
    return this._lastSearchAddressResult;
  }
  get lastRouteInstructionsResult(): GetRouteInstructionsResult | undefined {
    return this._lastRouteInstructionsResult;
  }
  get lastGeocodeQuery(): string | undefined {
    return this._lastGeocodeQuery;
  }
  get lastGeocodeResult(): SearchAddressResult | undefined {
    return this._lastGeocodeResult;
  }
  get lastReverseGeocodeQuery(): GeoJSON.Position | undefined {
    return this._lastReverseGeocodeQuery;
  }
  get lastReverseGeocodeResult(): SearchAddressResult[] | undefined {
    return this._lastReverseGeocodeResult;
  }
  get navigatorWatchPosition(): LatLonPosition | undefined {
    return this._navigatorWatchPosition;
  }

  get geobiasPosition(): Promise<LatLonPosition | undefined> {
    if (this._navigatorWatchPosition) {
      return Promise.resolve(this._navigatorWatchPosition);
    }
    return this._discoveryClient.getNetworkData().then((response) => {
      const data = response?.data;
      return new Promise((resolve) => {
        const networkPosition = {
          lat: data?.sites?.[0]?.address.latitude,
          lon: data?.sites?.[0]?.address.longitude,
        };
        if (networkPosition.lat && networkPosition.lon) {
          resolve(networkPosition);
        } else {
          throw new Error(`GeoService#geobiasPosition: No position found`);
        }
      });
    });
  }
}

/**
 * Singleton instance of the GeoService.
 * This instance is used to ensure that only one instance of the GeoService is created and used throughout the application.
 *
 * @private
 */
let _geoServiceInstance: GeoService;

/**
 * Factory function to create and return a new instance of GeoService.
 *
 * @param apiKey - The API key used to authenticate with the GeoService.
 * @returns A new instance of GeoService.
 */
export default function GeoServiceFactory(apiKey: string): GeoService {
  const service = new GeoService(apiKey);
  _geoServiceInstance = service;
  return service;
}

/**
 * Retrieves the singleton instance of the GeoService.
 *
 * @returns {GeoService} The singleton instance of the GeoService.
 */
export const getGeoService = (): GeoService => _geoServiceInstance;
